import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';

// Activity Log page. Admin-only audit feed across every mutating
// action in the system. Backend gates with requireRole('admin') so
// salespeople and staff can't reach this page even if they type the
// URL directly -- the API call 403s and the table just shows empty.
//
// Filter UX matches the Email Tracking page so the two feel
// consistent: dropdowns on top, table below, refresh on filter
// change. Date range presets cover the common "last week / month"
// cases; explicit From/To inputs handle anything else.

// Maps date-range preset name -> [from, to] ISO date strings. Computed
// fresh on each render so "Today" stays accurate over a session.
// Date preset definitions used by the quick-pick button row at the top
// of the page. Each entry has a key (matches state), display label, and
// a function returning [fromISO, toISO]. Order matters: it determines
// left-to-right button order in the UI.
// Format a seconds count as "Xh Ym" for the per-user time-worked badge.
// Uses session-detection logic on the server: gaps > 15 minutes start a
// new session, sessions of duration 0 (single events) round up to 5 min.
// Examples:
//   3725 sec -> "1h 2m"
//   320 sec  -> "5m"
//   0        -> "—"
function formatWorkedSeconds(sec) {
  const s = Number(sec) || 0;
  if (s <= 0) return '—';
  const totalMin = Math.round(s / 60);
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  if (h === 0) return `${m}m`;
  if (m === 0) return `${h}h`;
  return `${h}h ${m}m`;
}

const DATE_PRESETS = [
  { key: 'today',         label: 'Today' },
  { key: 'last_3_days',   label: 'Last 3 Days' },
  { key: 'last_7_days',   label: 'Last 7 Days' },
  { key: 'last_10_days',  label: 'Last 10 Days' },
  { key: 'last_30_days',  label: 'Last 30 Days' },
  { key: 'this_month',    label: 'This Month' },
  { key: 'last_month',    label: 'Last Month' },
  { key: 'all',           label: 'All Time' }
];

function presetRange(name) {
  const today = new Date();
  const iso = (d) => d.toISOString().slice(0, 10);
  const start = new Date(today);
  switch (name) {
    case 'today':         return [iso(today), iso(today)];
    case 'last_3_days':   start.setDate(today.getDate() - 2);  return [iso(start), iso(today)];
    case 'last_7_days':   start.setDate(today.getDate() - 6);  return [iso(start), iso(today)];
    case 'last_10_days':  start.setDate(today.getDate() - 9);  return [iso(start), iso(today)];
    case 'last_30_days':  start.setDate(today.getDate() - 29); return [iso(start), iso(today)];
    case 'this_month':    return [iso(new Date(today.getFullYear(), today.getMonth(), 1)), iso(today)];
    case 'last_month': {
      const first = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      const last  = new Date(today.getFullYear(), today.getMonth(), 0);
      return [iso(first), iso(last)];
    }
    case 'all':           return ['', ''];
    default:              return ['', ''];
  }
}

// Tone (CSS color) per action type for the compact action chip.
function actionTone(action) {
  if (action === 'create')      return { bg: '#d1fae5', fg: '#047857' };
  if (action === 'update')      return { bg: '#dbeafe', fg: '#1e40af' };
  if (action === 'delete')      return { bg: '#fee2e2', fg: '#b91c1c' };
  if (action === 'send_email')  return { bg: '#fef3c7', fg: '#b45309' };
  return { bg: '#f3f4f6', fg: '#374151' };
}

function fmtDateTime(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString('en-CA', {
    year: 'numeric', month: 'short', day: '2-digit',
    hour: '2-digit', minute: '2-digit'
  });
}

// Render the JSONB `changes` payload as a tiny stacked list. For update
// rows the shape is { field: [before, after] }. For create/delete the
// shape is a flat snapshot object -- shown as a compact key:value
// listing.
function ChangesCell({ action, changes }) {
  if (!changes || Object.keys(changes).length === 0) return null;
  // Dates land in changes as either ISO strings (date columns) or the
  // verbose "Wed May 12 2027 00:00:00 GMT+0000 (Coor..." form produced
  // when activityLogger.diff stores a JS Date directly. Detect both and
  // render as a clean YYYY-MM-DD.
  function isDateLike(v) {
    if (typeof v !== 'string') return false;
    if (/^\d{4}-\d{2}-\d{2}/.test(v)) return true;
    return /^(Mon|Tue|Wed|Thu|Fri|Sat|Sun) [A-Z][a-z]{2} \d{2} \d{4}/.test(v);
  }
  const trunc = (v, n = 32) => {
    if (v === null || v === undefined) return 'null';
    if (isDateLike(v)) {
      const d = new Date(v);
      if (!Number.isNaN(d.getTime())) return d.toISOString().slice(0, 10);
    }
    const s = String(v);
    return s.length > n ? `${s.slice(0, n - 1)}…` : s;
  };

  if (action === 'update') {
    const fields = Object.entries(changes);
    return (
      <div style={{ fontSize: 'var(--text-small)', color: 'var(--color-text-dim)' }}>
        {fields.map(([field, [before, after]]) => (
          <div key={field} style={{ marginTop: 2, fontFamily: 'var(--font-mono)', fontSize: '11px' }}>
            <span style={{ fontWeight: 'var(--font-semibold)', color: 'var(--color-text)' }}>{field}</span>
            : <span style={{ color: '#b91c1c' }}>{trunc(before)}</span>
            <span style={{ margin: '0 4px' }}>→</span>
            <span style={{ color: '#047857' }}>{trunc(after)}</span>
          </div>
        ))}
      </div>
    );
  }
  // For non-update events show the snapshot as small mono kv lines.
  const entries = Object.entries(changes).slice(0, 6);
  return (
    <div style={{ fontSize: 'var(--text-small)', color: 'var(--color-text-dim)', fontFamily: 'var(--font-mono)', fontSize: '11px' }}>
      {entries.map(([k, v]) => (
        <div key={k}>
          <span style={{ fontWeight: 'var(--font-semibold)', color: 'var(--color-text)' }}>{k}</span>: {trunc(v)}
        </div>
      ))}
    </div>
  );
}

// Tone (background + text) for the summary cards. Each user gets the
// same colour every visit -- derived deterministically from the user_id
// so Sheldon's card is always teal, Mary's always purple, etc.
const SUMMARY_PALETTE = [
  { bg: '#cffafe', fg: '#155e75', accent: '#0e7490' }, // teal
  { bg: '#ede9fe', fg: '#5b21b6', accent: '#7c3aed' }, // purple
  { bg: '#d1fae5', fg: '#065f46', accent: '#047857' }, // green
  { bg: '#fef3c7', fg: '#92400e', accent: '#b45309' }, // amber
  { bg: '#dbeafe', fg: '#1e3a8a', accent: '#1e40af' }, // blue
  { bg: '#fce7f3', fg: '#831843', accent: '#be185d' }  // pink
];
function paletteFor(userId) {
  if (!userId) return { bg: '#f3f4f6', fg: '#6b7280', accent: '#6b7280' }; // muted grey for (system)
  return SUMMARY_PALETTE[userId % SUMMARY_PALETTE.length];
}

export default function ActivityLog() {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [users, setUsers] = useState([]);
  const [summary, setSummary] = useState([]);

  // Filters
  const [userId, setUserId] = useState('');
  const [datePreset, setDatePreset] = useState('last_7_days');
  const [from, to] = useMemo(() => presetRange(datePreset), [datePreset]);
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [entityType, setEntityType] = useState('');
  const [action, setAction] = useState('');
  const [q, setQ] = useState('');

  // Expanded rows for the diff drawer
  const [expanded, setExpanded] = useState(new Set());

  // Effective from/to: custom dates take precedence over the preset.
  const effFrom = customFrom || from;
  const effTo   = customTo   || to;

  useEffect(() => {
    axios.get('/api/activity-log/users')
      .then((r) => setUsers(r.data?.users || []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const params = {};
    if (userId)     params.user_id     = userId;
    if (effFrom)    params.from        = effFrom;
    if (effTo)      params.to          = effTo;
    if (entityType) params.entity_type = entityType;
    if (action)     params.action      = action;
    if (q)          params.q           = q;
    axios.get('/api/activity-log', { params })
      .then((r) => { if (!cancelled) setEntries(r.data?.entries || []); })
      .catch((e) => { if (!cancelled) setError(e.response?.data?.error || e.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [userId, effFrom, effTo, entityType, action, q]);

  // Per-user summary stats. Uses the same filters as the table BUT
  // omits the user_id filter -- you always want to see ALL users in
  // the summary row, even when the table below is scoped to one.
  // Click a card to scope the table to that user; click the active
  // card (or use the User dropdown) to clear.
  useEffect(() => {
    let cancelled = false;
    const params = {};
    if (effFrom)    params.from        = effFrom;
    if (effTo)      params.to          = effTo;
    if (entityType) params.entity_type = entityType;
    if (action)     params.action      = action;
    if (q)          params.q           = q;
    axios.get('/api/activity-log/summary-by-user', { params })
      .then((r) => { if (!cancelled) setSummary(r.data?.users || []); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [effFrom, effTo, entityType, action, q]);

  const exportUrl = useMemo(() => {
    const params = new URLSearchParams();
    if (userId)     params.set('user_id',     userId);
    if (effFrom)    params.set('from',        effFrom);
    if (effTo)      params.set('to',          effTo);
    if (entityType) params.set('entity_type', entityType);
    if (action)     params.set('action',      action);
    if (q)          params.set('q',           q);
    return `/api/activity-log/export.csv?${params.toString()}`;
  }, [userId, effFrom, effTo, entityType, action, q]);

  function toggleExpand(id) {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  return (
    <section className="page">
      <div className="page-header">
        <div>
          <h2>Activity Log</h2>
          <div className="subtitle">
            Audit feed of every create, update, delete and email-send action across the system.
            Admin-only.
          </div>
        </div>
        <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
          <a href={exportUrl} className="btn-secondary" download>
            Export CSV
          </a>
        </div>
      </div>

      {/* Team total hours across all non-system users for the current
          filter window. Useful for "how much team-time went into work
          this week?" When the table is currently filtered to a single
          user this still shows the team-wide total -- by design, so
          it's a fixed reference point regardless of card selection. */}
      {summary.length > 0 && (() => {
        const teamSeconds = summary
          .filter((s) => s.user_id > 0)
          .reduce((sum, s) => sum + (Number(s.total_seconds) || 0), 0);
        if (teamSeconds <= 0) return null;
        return (
          <div style={{
            marginBottom: 'var(--space-3)',
            padding: '10px 14px',
            background: 'var(--color-surface)',
            border: '1px solid var(--color-border)',
            borderRadius: 'var(--radius-md)',
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-3)'
          }}>
            <span style={{
              fontFamily: 'var(--font-mono)',
              fontSize: '11px',
              color: 'var(--color-text-dim)',
              textTransform: 'uppercase',
              letterSpacing: '0.04em'
            }}>
              Team time worked
            </span>
            <span style={{
              fontFamily: 'var(--font-mono)',
              fontSize: '18px',
              fontWeight: 'var(--font-bold)',
              color: 'var(--color-accent)'
            }}>
              {formatWorkedSeconds(teamSeconds)}
            </span>
            <span style={{
              fontFamily: 'var(--font-mono)',
              fontSize: '11px',
              color: 'var(--color-text-dim)'
            }}>
              across {summary.filter((s) => s.user_id > 0 && s.total_seconds > 0).length} active user(s)
              · session gap threshold 15 min
            </span>
          </div>
        );
      })()}

      {/* Summary by user row -- per-user totals + action breakdown for
          the current filter window. Click a card to scope the table to
          that user; the active card highlights so it's clear what's
          filtered. The total in the upper-right reflects this filter
          set so Dave can see "47 actions this week" at a glance. */}
      {summary.length > 0 && (
        <div style={{
          display: 'flex',
          gap: 'var(--space-3)',
          flexWrap: 'wrap',
          marginBottom: 'var(--space-3)'
        }}>
          {summary.map((s) => {
            const tone = paletteFor(s.user_id);
            const isActive = String(userId) === String(s.user_id);
            const isSystem = !s.user_id;
            return (
              <button
                key={s.user_id}
                type="button"
                onClick={() => setUserId(isActive ? '' : (isSystem ? '' : String(s.user_id)))}
                disabled={isSystem}
                title={isSystem ? 'System-generated activity (no logged-in user)' : (isActive ? 'Click to clear filter' : `Show only ${s.user_name}'s activity`)}
                style={{
                  background: isActive ? tone.accent : tone.bg,
                  color:      isActive ? '#fff'      : tone.fg,
                  border: `1px solid ${tone.accent}`,
                  borderRadius: 'var(--radius-lg, 8px)',
                  padding: '12px 18px',
                  textAlign: 'left',
                  cursor: isSystem ? 'default' : 'pointer',
                  minWidth: 180,
                  flex: '0 0 auto',
                  opacity: isSystem ? 0.7 : 1,
                  fontFamily: 'inherit',
                  transition: 'background 0.15s, color 0.15s'
                }}
              >
                <div style={{
                  fontSize: 'var(--text-small)',
                  fontWeight: 'var(--font-semibold)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.04em',
                  opacity: 0.9
                }}>
                  {s.user_name}
                </div>
                <div style={{
                  fontSize: '28px',
                  fontFamily: 'var(--font-mono)',
                  fontWeight: 'var(--font-bold)',
                  lineHeight: 1.1,
                  marginTop: 2
                }}>
                  {s.total}
                </div>
                {/* Hours-worked row. Session-based: consecutive activity
                    within 15 minutes counts as one session; sum across
                    sessions = work time. Tooltip shows session count so
                    Dave can sanity-check long stretches vs many short
                    bursts. Hidden for the (system) row -- nobody
                    "worked" those. */}
                {s.user_id > 0 && s.total_seconds > 0 && (
                  <div
                    style={{
                      fontSize: '12px',
                      fontFamily: 'var(--font-mono)',
                      fontWeight: 'var(--font-semibold)',
                      marginTop: 2,
                      opacity: 0.95
                    }}
                    title={
                      `${formatWorkedSeconds(s.total_seconds)} of work` +
                      ` across ${s.session_count} session${s.session_count === 1 ? '' : 's'}` +
                      (s.first_event ? `\nFirst event: ${new Date(s.first_event).toLocaleString()}` : '') +
                      (s.last_event  ? `\nLast event:  ${new Date(s.last_event).toLocaleString()}`  : '')
                    }
                  >
                    {formatWorkedSeconds(s.total_seconds)} worked
                  </div>
                )}
                <div style={{
                  fontSize: '11px',
                  fontFamily: 'var(--font-mono)',
                  marginTop: 4,
                  opacity: 0.85
                }}>
                  {s.creates  > 0 && <span style={{ marginRight: 8 }}>{s.creates} new</span>}
                  {s.updates  > 0 && <span style={{ marginRight: 8 }}>{s.updates} edit</span>}
                  {s.deletes  > 0 && <span style={{ marginRight: 8 }}>{s.deletes} del</span>}
                  {s.sends    > 0 && <span style={{ marginRight: 8 }}>{s.sends} send</span>}
                  {s.other    > 0 && <span style={{ marginRight: 8 }}>{s.other} other</span>}
                </div>
              </button>
            );
          })}
        </div>
      )}

      {/* Date range quick-pick row. Pills, not a dropdown -- Dave's
          preference (more discoverable, one click to switch). Active
          preset gets a solid teal background. Selecting a custom From/To
          below clears the preset so the UI is unambiguous about what's
          currently filtering the data. */}
      <div style={{
        display: 'flex',
        gap: 'var(--space-2)',
        flexWrap: 'wrap',
        alignItems: 'center',
        marginBottom: 'var(--space-3)'
      }}>
        <span className="result-count" style={{ marginRight: 'var(--space-1)' }}>Date range:</span>
        {DATE_PRESETS.map((p) => {
          const active = datePreset === p.key && !customFrom && !customTo;
          return (
            <button
              key={p.key}
              type="button"
              onClick={() => { setDatePreset(p.key); setCustomFrom(''); setCustomTo(''); }}
              style={{
                padding: '6px 14px',
                background: active ? 'var(--color-accent, #0e7490)' : 'var(--color-surface, #fff)',
                color:      active ? '#fff'                          : 'var(--color-text, #1a202c)',
                border: `1px solid ${active ? 'var(--color-accent, #0e7490)' : 'var(--color-border, #e4e7eb)'}`,
                borderRadius: 'var(--radius-pill, 999px)',
                cursor: 'pointer',
                fontWeight: active ? 'var(--font-semibold)' : 'var(--font-regular)',
                fontSize: 'var(--text-small)',
                transition: 'background 0.15s, color 0.15s'
              }}
            >
              {p.label}
            </button>
          );
        })}
      </div>

      {/* Filter row: who, what, custom dates, search. */}
      <div className="panel" style={{ padding: 'var(--space-3)', marginBottom: 'var(--space-3)' }}>
        <div style={{ display: 'flex', gap: 'var(--space-3)', flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <span className="result-count">User</span>
            <select value={userId} onChange={(e) => setUserId(e.target.value)}>
              <option value="">All users</option>
              {users.map((u) => (
                <option key={u.id} value={u.id}>{u.name || u.email}</option>
              ))}
            </select>
          </label>

          <label style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <span className="result-count">Entity</span>
            <select value={entityType} onChange={(e) => setEntityType(e.target.value)}>
              <option value="">All entities</option>
              <option value="customer">Customer</option>
              <option value="lead">Lead</option>
              <option value="site">Site</option>
              <option value="ticket">Ticket</option>
              <option value="distributor">Distributor</option>
              <option value="strategic_account">Strategic account</option>
              <option value="vendor_contact">Vendor contact</option>
              <option value="note">Note</option>
            </select>
          </label>

          <label style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <span className="result-count">Action</span>
            <select value={action} onChange={(e) => setAction(e.target.value)}>
              <option value="">All actions</option>
              <option value="create">Create</option>
              <option value="update">Update</option>
              <option value="delete">Delete</option>
              <option value="send_email">Send email</option>
              <option value="send_batch_email">Send batch email</option>
              <option value="add_note">Add note</option>
              <option value="import">Import</option>
            </select>
          </label>

          {/* Custom-date escape hatch. Use the pills above for common
              ranges; come down here only when you need something
              specific. Typing in either field overrides the active
              pill (clears the preset highlight). */}
          <label style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <span className="result-count">Custom from</span>
            <input type="date" value={customFrom} onChange={(e) => setCustomFrom(e.target.value)} />
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <span className="result-count">Custom to</span>
            <input type="date" value={customTo} onChange={(e) => setCustomTo(e.target.value)} />
          </label>

          <label style={{ display: 'flex', flexDirection: 'column', gap: 2, flex: '1 1 200px', minWidth: 200 }}>
            <span className="result-count">Search</span>
            <input
              type="search"
              placeholder="Company, field, value…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
          </label>
        </div>
      </div>

      {error && <p className="error">{error}</p>}

      <div className="panel" style={{ overflow: 'hidden', padding: 0 }}>
        <table className="data-table" style={{ width: '100%' }}>
          <thead>
            <tr>
              <th style={{ textAlign: 'left', width: 150 }}>When</th>
              <th style={{ textAlign: 'left', width: 140 }}>User</th>
              <th style={{ textAlign: 'left', width: 100 }}>Action</th>
              <th style={{ textAlign: 'left' }}>Entity</th>
              <th style={{ textAlign: 'left' }}>Summary / Changes</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={5} style={{ textAlign: 'center', padding: 'var(--space-6)' }}>Loading…</td></tr>
            )}
            {!loading && entries.length === 0 && (
              <tr><td colSpan={5} style={{ textAlign: 'center', padding: 'var(--space-6)', color: 'var(--color-text-dim)' }}>
                No activity in this range.
              </td></tr>
            )}
            {!loading && entries.map((e) => {
              const tone = actionTone(e.action);
              const isOpen = expanded.has(e.id);
              return (
                <tr key={e.id} style={{ cursor: e.changes ? 'pointer' : 'default' }}
                    onClick={() => e.changes && toggleExpand(e.id)}>
                  <td className="cell-mono" style={{ whiteSpace: 'nowrap' }}>{fmtDateTime(e.occurred_at)}</td>
                  <td>{e.user_name || <span style={{ color: 'var(--color-text-faint)' }}>—</span>}</td>
                  <td>
                    <span style={{
                      display: 'inline-block',
                      padding: '2px 8px',
                      background: tone.bg,
                      color: tone.fg,
                      borderRadius: 'var(--radius-sm, 4px)',
                      fontFamily: 'var(--font-mono)',
                      fontSize: '10.5px',
                      fontWeight: 'var(--font-semibold)',
                      textTransform: 'uppercase',
                      letterSpacing: '0.04em'
                    }}>{e.action.replace('_', ' ')}</span>
                  </td>
                  <td>
                    {(() => {
                      if (!e.entity_type || !e.entity_id) {
                        return <span style={{ color: 'var(--color-text-faint)' }}>—</span>;
                      }
                      const label = e.entity_label || `${e.entity_type} #${e.entity_id}`;
                      // Route entity_type to the right detail page. Was
                      // hard-coded to /customers/:id which 404'd on
                      // leads, tickets, distributors, etc. Entity types
                      // without a detail page (site, note, strategic_account,
                      // vendor_contact) render as plain text -- linking
                      // to a non-existent route is worse than no link.
                      const path = (() => {
                        switch (e.entity_type) {
                          case 'customer':    return `/customers/${e.entity_id}`;
                          case 'lead':        return `/leads/${e.entity_id}`;
                          case 'ticket':      return `/tickets/${e.entity_id}`;
                          case 'distributor': return `/distributors/${e.entity_id}`;
                          // Sites have no detail page of their own; the
                          // server attaches parent_customer_id on site
                          // rows so we can route to the parent. Fall
                          // through to plain text if the join failed
                          // (e.g. the site was deleted).
                          case 'site':
                            return e.parent_customer_id
                              ? `/customers/${e.parent_customer_id}`
                              : null;
                          default:            return null;
                        }
                      })();
                      if (!path) {
                        return (
                          <span
                            style={{ color: 'var(--color-text)', fontWeight: 'var(--font-semibold)' }}
                            title={`${e.entity_type} #${e.entity_id} (no detail page)`}
                          >
                            {label}
                          </span>
                        );
                      }
                      return (
                        <Link
                          to={path}
                          onClick={(ev) => ev.stopPropagation()}
                          style={{ color: 'var(--color-text)', textDecoration: 'none', fontWeight: 'var(--font-semibold)' }}
                          title={`${e.entity_type} #${e.entity_id}`}
                        >
                          {label}
                        </Link>
                      );
                    })()}
                  </td>
                  <td>
                    <div>{e.summary}</div>
                    {isOpen && <ChangesCell action={e.action} changes={e.changes} />}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {!loading && (
        <div className="result-count" style={{ marginTop: 'var(--space-2)' }}>
          Showing {entries.length} entries · click a row to expand its change details
        </div>
      )}
    </section>
  );
}
